import React, { useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { leadCommunicationAPI, emailAttachmentAPI } from '../lib/api';
import { Send, Paperclip, X, FileText, Loader2 } from 'lucide-react';

export default function SendEmailModal({ isOpen, onClose, leadId, leadEmail, leadName }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const totalSize = files.reduce((s, f) => s + f.size, 0);
    if (totalSize > 25 * 1024 * 1024) {
      toast.error('Total file size exceeds 25 MB limit');
      return;
    }

    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await emailAttachmentAPI.upload(formData);
        uploaded.push({
          id: res.id,
          filename: res.filename,
          size: res.size,
          download_url: res.download_url,
        });
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setAttachments((prev) => [...prev, ...uploaded]);
    setUploading(false);
    e.target.value = '';
  }, []);

  const removeAttachment = (id) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setSending(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const attachmentUrls = attachments.map((a) => `${backendUrl}${a.download_url}`);
      await leadCommunicationAPI.sendEmail(leadId, {
        subject,
        message,
        attachment_urls: attachmentUrls,
      });
      toast.success(`Email sent to ${leadEmail}`);
      setSubject('');
      setMessage('');
      setAttachments([]);
      onClose();
    } catch (err) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-[520px]" data-testid="send-email-modal">
        <SheetHeader>
          <SheetTitle>Send Email to {leadName}</SheetTitle>
          <p className="text-sm text-muted-foreground">{leadEmail}</p>
        </SheetHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="email-subject">Subject</Label>
            <Input
              id="email-subject"
              data-testid="email-subject-input"
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="email-message">Message</Label>
            <Textarea
              id="email-message"
              data-testid="email-message-input"
              placeholder="Write your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[180px]"
            />
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Attachments</Label>
              <label
                htmlFor="email-file-upload"
                className="inline-flex items-center gap-1.5 text-sm text-primary cursor-pointer hover:underline"
              >
                <Paperclip className="h-3.5 w-3.5" />
                {uploading ? 'Uploading...' : 'Attach file'}
                <input
                  id="email-file-upload"
                  data-testid="email-file-upload"
                  type="file"
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.json,.zip,.jpg,.jpeg,.png,.gif,.webp"
                />
              </label>
            </div>
            {attachments.length > 0 && (
              <div className="space-y-1.5" data-testid="email-attachments-list">
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className="flex items-center justify-between px-3 py-2 bg-muted rounded-md text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{att.filename}</span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        {formatSize(att.size)}
                      </span>
                    </div>
                    <button
                      onClick={() => removeAttachment(att.id)}
                      className="ml-2 p-0.5 hover:bg-background rounded"
                      data-testid={`remove-attachment-${att.id}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            onClick={handleSend}
            disabled={sending || uploading}
            className="w-full gap-2"
            data-testid="send-email-btn"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            {sending ? 'Sending...' : 'Send Email'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
